import { jsx } from 'react/jsx-runtime';
import { createContext, useContext } from 'react';
import { EmployeePermissionFlags } from '@squareup/shared-types-protos/squareup/employees/employees.js';
import { RedirectRequiredError } from '@squareup/util-common-errors';
import { isHostedServerEnvironment } from '@squareup/util-environment';

const SessionContext = createContext(null);
function SessionProvider({ children, session = getSession() }) {
  return /* @__PURE__ */ jsx(SessionContext.Provider, { value: session, children });
}
function useSession() {
  const session = useContext(SessionContext);
  if (!session) {
    throw new Error("The useSession hook must be used within a SessionProvider.");
  }
  return session;
}
function withSessionProvider(WrappedComponent) {
  return function WithSessionComponent(props) {
    return /* @__PURE__ */ jsx(SessionProvider, { children: /* @__PURE__ */ jsx(WrappedComponent, { ...props }) });
  };
}
function getSession() {
  return globalThis.window?.__dashboardSession;
}
class InvalidSessionError extends Error {
  constructor(message) {
    super(`Invalid session type: ${message}`);
  }
}
function isPersonEmploymentSession(session) {
  return typeof session.merchantToken === "string" && typeof session.employmentToken === "string" && session.employment !== void 0;
}
function usePersonEmploymentSession() {
  const session = useSession();
  if (!isPersonEmploymentSession(session)) {
    throw new InvalidSessionError("Expected a PersonEmploymentSession");
  }
  return session;
}
function getPersonEmploymentSession() {
  const session = getSession();
  if (!isPersonEmploymentSession(session)) {
    throw new InvalidSessionError("Expected a PersonEmploymentSession");
  }
  return session;
}

const Permissions = EmployeePermissionFlags;
function hasPermission(session, permission) {
  return session.employment?.permissions.includes(permission) ?? false;
}
function hasPermissions(session, permissions) {
  return permissions.every((permission) => hasPermission(session, permission));
}

async function fetchSession({
  target = "https://app.squareupstaging.com",
  cookie,
  postLoginUrl = "/dashboard",
  languageCode,
  speleoTraceId
}) {
  const fetchImplementation = await getFetchImplementation();
  const suffix = "/dashboard/_session";
  const app = "dashboard";
  const url = isHostedServerEnvironment() ? suffix : `${target}${suffix}`;
  const options = {
    app,
    // Attempting to address ECONNRESET errors: https://github.com/squareup/node-service-container/pull/95
    socketTimeout: 35e3,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      cookie,
      ...speleoTraceId ? { "x-speleo-trace-id": speleoTraceId } : {},
      ...languageCode ? { "accept-language": languageCode } : {}
    },
    redirect: "manual"
  };
  const response = await fetchImplementation(url, options);
  if (response.redirected || response.status === 302) {
    const targetUrl = new URL(target);
    targetUrl.pathname = "/login";
    const redirectUrlString = response.headers.get("location") ?? targetUrl.toString();
    const redirectUrl = new URL(redirectUrlString);
    redirectUrl.protocol = targetUrl.protocol;
    redirectUrl.hostname = targetUrl.hostname;
    redirectUrl.port = targetUrl.port;
    redirectUrl.searchParams.set("return_to", postLoginUrl);
    const urlToUse = redirectUrl.toString();
    throw new RedirectRequiredError("The session was not created correctly. A redirect is required to complete sign-in.", urlToUse);
  }
  if (!response.ok) {
    throw new Error(`Failed to receive session: ${response.statusText} - ${response.status}`);
  }
  const session = await response.json();
  return session;
}
async function getFetchImplementation() {
  if (isHostedServerEnvironment()) {
    const mod = await import('@squareup/container-connector');
    return mod.fetch;
  }
  return globalThis.fetch;
}

function scriptTagForSession(session) {
  return `<script>window.__dashboardSession = ${JSON.stringify(session)};<\/script>`;
}

export { Permissions, SessionProvider, fetchSession, getPersonEmploymentSession, getSession, hasPermission, hasPermissions, scriptTagForSession, usePersonEmploymentSession, useSession, withSessionProvider };
